import { DateTime } from 'luxon';
import { TransactionType } from '../api/transaction-types';
import { useTransactions } from '../api/use-get-transactions';
import { useTimeZone } from '../../user/api/use-get-user';
import { useMemo } from 'react';
import { toYearMonth } from '../../utils/format';
import { useBucketsMap } from '../../bucket/api/use-get-buckets';
import { useAccountsMap } from '../../account/api/use-get-accounts';
import { AccountType } from '../../account/api/account-types';
// Special bucket IDs for our pseudo-categories
export const UNCATEGORIZED_BUCKET_CATEGORY_ID = 'uncategorized';
// Set to track archived bucket IDs - will be populated during buildTransactionSummary
export const archivedBucketIds = new Set();
/**
 * Hook for generating transaction summary data for income vs expense reporting
 * @param targetYear - The year for which to generate the summary
 * @returns Transaction summary with pre-calculated monthly totals
 */
export function useTransactionSummary(targetYear) {
    const transactions = useTransactions();
    const timeZone = useTimeZone();
    const accountsMap = useAccountsMap();
    const bucketsMap = useBucketsMap();
    // Build the summary using useMemo to avoid unnecessary recalculations
    return useMemo(() => {
        return buildTransactionSummary(transactions, targetYear, timeZone, accountsMap, bucketsMap);
    }, [transactions, targetYear, timeZone, accountsMap, bucketsMap]);
}
/**
 * Builds a transaction summary for efficient report generation
 * @param transactions - Array of transactions sorted in descending order by date
 * @param targetYear - The year for which we're generating the report
 * @param timeZone - User's timezone for proper date handling
 * @param accountsMap - Map of all accounts including archived ones
 * @param bucketsMap - Map of all buckets including archived ones
 * @returns Transaction summary with pre-calculated totals
 */
export function buildTransactionSummary(transactions, targetYear, timeZone, accountsMap, bucketsMap) {
    const summary = {
        bucketMonthlyTotals: {},
        payeeMonthlyTotals: {},
        overallMonthlyTotals: {},
        archivedBucketIds: new Set(),
    };
    // We need to look back at most 12 months from the target year
    const earliestYear = targetYear - 1;
    // Process transactions until we're past the earliest relevant year
    for (const transaction of transactions) {
        // Skip transfers
        if (transaction.type === TransactionType.TRANSFER) {
            continue;
        }
        const account = accountsMap.get(transaction.accountId);
        const isOffBudget = account?.type === AccountType.OFF_BUDGET_ASSET || account?.type === AccountType.OFF_BUDGET_LIABILITY;
        if (isOffBudget) {
            continue;
        }
        // Convert epoch to DateTime in user's timezone
        const transactionDate = DateTime.fromSeconds(transaction.dateEpoch).setZone(timeZone);
        const year = transactionDate.year;
        // Stop processing once we've passed the earliest relevant year
        if (year < earliestYear) {
            break;
        }
        // Extract year-month string for summary key
        const yearMonth = toYearMonth(transactionDate.year, transactionDate.month);
        // Income transactions (inflow only, no bucket)
        if (transaction.inflow && !transaction.bucketId) {
            processIncomeTransaction(summary, transaction, yearMonth);
        }
        // Bucketed transactions (expense or uncategorized/archived)
        if (!transaction.bucketId) {
            // Pure-expense uncategorized
            if (transaction.outflow && transaction.outflow > 0) {
                processBucketedTransaction(summary, UNCATEGORIZED_BUCKET_CATEGORY_ID, transaction, yearMonth);
            }
        }
        else {
            // Has a bucketId: determine where it belongs
            const bucket = bucketsMap.get(transaction.bucketId);
            if (!bucket) {
                // Non-existent bucket ID - use uncategorized
                processBucketedTransaction(summary, UNCATEGORIZED_BUCKET_CATEGORY_ID, transaction, yearMonth);
            }
            else {
                // Always process with the actual bucket ID
                processBucketedTransaction(summary, transaction.bucketId, transaction, yearMonth);
                // But track if the bucket is archived
                if (bucket.archived) {
                    summary.archivedBucketIds.add(transaction.bucketId);
                }
            }
        }
        // Update overall totals
        updateOverallTotals(summary, transaction, yearMonth);
    }
    return summary;
}
/**
 * DRY helper for any bucketed transaction (normal, archived, or uncategorized)
 * @param summary - Transaction summary to update
 * @param bucketId - Target bucket ID or pseudo-category ID
 * @param transaction - Transaction to process
 * @param yearMonth - Year-month string for summary lookup
 */
export function processBucketedTransaction(summary, bucketId, transaction, yearMonth) {
    if (!summary.bucketMonthlyTotals[bucketId]) {
        summary.bucketMonthlyTotals[bucketId] = {};
    }
    if (!summary.bucketMonthlyTotals[bucketId][yearMonth]) {
        summary.bucketMonthlyTotals[bucketId][yearMonth] = {
            inflow: 0,
            outflow: 0,
            netExpense: 0,
        };
    }
    const totals = summary.bucketMonthlyTotals[bucketId][yearMonth];
    totals.inflow += transaction.inflow || 0;
    totals.outflow += transaction.outflow || 0;
    totals.netExpense += (transaction.outflow || 0) - (transaction.inflow || 0);
}
/**
 * Processes an income transaction and updates the summary
 * @param summary - Transaction summary to update
 * @param transaction - Income transaction to process
 * @param yearMonth - Year-month string for summary lookup
 */
export function processIncomeTransaction(summary, transaction, yearMonth) {
    const payee = transaction.payee || 'Unknown';
    const existingPayeeKey = Object.keys(summary.payeeMonthlyTotals).find((key) => key.toLowerCase() === payee.toLowerCase());
    const payeeKey = existingPayeeKey || payee;
    if (!summary.payeeMonthlyTotals[payeeKey]) {
        summary.payeeMonthlyTotals[payeeKey] = {};
    }
    if (!summary.payeeMonthlyTotals[payeeKey][yearMonth]) {
        summary.payeeMonthlyTotals[payeeKey][yearMonth] = { inflow: 0 };
    }
    summary.payeeMonthlyTotals[payeeKey][yearMonth].inflow += transaction.inflow || 0;
}
/**
 * Updates overall monthly totals in the summary
 * @param summary - Transaction summary to update
 * @param transaction - Transaction to add to overall totals
 * @param yearMonth - Year-month string for summary lookup
 */
export function updateOverallTotals(summary, transaction, yearMonth) {
    if (!summary.overallMonthlyTotals[yearMonth]) {
        summary.overallMonthlyTotals[yearMonth] = {
            totalInflow: 0,
            totalOutflow: 0,
        };
    }
    summary.overallMonthlyTotals[yearMonth].totalInflow += transaction.inflow || 0;
    summary.overallMonthlyTotals[yearMonth].totalOutflow += transaction.outflow || 0;
}
